<template>
  <div v-if="technicianPayment">
    <base-page-heading
      :title="title"
      show-back-button
    >
      <template #secondary-title>
        <status-badge
          v-if="technicianPayment.status"
          :status="technicianPayment.status"
          :status-variants="statusVariants"
        />
      </template>
    </base-page-heading>

    <div class="content pt-lg-5">
      <b-row class="mb-5">
        <b-col
          md="12"
          lg="6"
        >
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Period
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ technicianPayment | dateRangeFormat("start_date", "end_date") }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Due Date
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ technicianPayment.due_date | dateFormat }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Emailed
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="technicianPayment.emailed_at">
                  {{ technicianPayment.emailed_at | dateTimeFormat }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Total
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <money-format
                    :value="technicianPayment.balance.amount"
                    :currency-code="technicianPayment.balance.currency"
                    subunits-value
                  />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>

        <b-col
          md="12"
          lg="6"
        >
          <b-row>
            <b-col
              cols="12"
              sm="7"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Technician
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :to="{ name: 'technician-user', params: { id: user.id } }">
                    {{ user.name }}
                  </b-link>
                </b-col>
              </b-row>

              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Address
                </b-col>
              </b-row>

              <b-row>
                <b-col>
                  <div>{{ user.address }}</div>
                  <div>
                    {{
                      {
                        city: user.city,
                        state: user.state,
                        zip_code: user.zip_code,
                      } | locationFormat
                    }}
                  </div>
                </b-col>
              </b-row>
            </b-col>
            <b-col
              cols="12"
              sm="5"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Email
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :href="`mailto:${user.email}`">
                    {{ user.email }}
                  </b-link>
                </b-col>
              </b-row>

              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Phone
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :href="`tel:${user.phone}`">
                    {{ user.phone }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row
        v-if="showCompletedBySummary"
        class="mb-5"
      >
        <b-col lg="6">
          <b-row class="text-muted font-w500 font-size-sm mb-1">
            <b-col>
              Completed By Summary
            </b-col>
          </b-row>

          <b-skeleton v-if="isLoadingData" />
          <b-row
            v-else
            v-for="(summary, index) in completedBySummary"
            :key="index"
            class="mb-2"
          >
            <b-col>
              <b-link
                :to="{ name: 'technician-user', params: { id: summary.done_by_id } }"
              >
                {{ summary.done_by }}
              </b-link>
            </b-col>
            <b-col class="text-right">
              <money-format
                :value="summary.amount_cents"
                subunits-value
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col lg="12">
          <technician-payment-items-list-filter
            :technician-payment="technicianPayment"
            :initial-filters="filters"
            @filters-applied="applyFilters"
            @data-changed="refreshTable"
          />
          <technician-payment-items-table
            :technician-payment-id="$route.params.id.toString()"
            :append-to-query="false"
            :filters="filters"
            :total="technicianPayment.balance"
            :key="tableKey"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import TechnicianPaymentItemsTable from '@/components/technician/technicianPaymentItems/Table.vue';
import TechnicianPaymentItemsListFilter from '@/components/technician/technicianPaymentItems/ListFilter.vue';
import technicianPaymentsMixin from '@/mixins/technicianPaymentsMixin';
import StatusBadge from '@/components/shared/StatusBadge.vue';
import { STATUS_VARIANTS } from '@/constants/technicianPayments';

export default {
  name: 'TechnicianUserPaymentsShow',
  mixins: [technicianPaymentsMixin],

  components: {
    TechnicianPaymentItemsTable,
    TechnicianPaymentItemsListFilter,
    StatusBadge,
  },
  data() {
    return {
      isLoadingData: false,
      technicianPayment: null,
      completedBySummary: [],
      statusVariants: STATUS_VARIANTS,
      tableKey: Date.now(),
      filters: {
        text: this.text,
      },
    };
  },
  mounted() {
    this.getTechnicianPayment();
    this.getCompletedBySummary();
  },
  computed: {
    title() {
      return `Payment ${this.technicianPayment.number}`;
    },
    user() {
      return this.technicianPayment.user;
    },
    showCompletedBySummary() {
      return this.completedBySummary.length > 1;
    },
  },
  methods: {
    getTechnicianPayment() {
      this.$technicianUserPaymentsAPI
        .get(this.$route.params.id)
        .then((technicianPayment) => (this.technicianPayment = technicianPayment))
        .catch(() => {
          this.$router.push('/404');
        });
    },
    getCompletedBySummary() {
      this.isLoadingData = true;
      this.$technicianUserPaymentsAPI
        .getCompletedBySummary(this.$route.params.id)
        .then((response) => {
          this.completedBySummary = response.data;
        })
        .catch(() => {
          this.completedBySummary = [];
        })
        .finally(() => {
          this.isLoadingData = false;
        });
    },
    refreshTable() {
      this.tableKey = Date.now();
    },
    applyFilters({ filters }) {
      this.filters = Object.assign({}, filters);
    },
  },
};
</script>
